import { Button, Center, Flex, Icon, Menu, MenuButton, MenuItem, MenuList, Spinner, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import { VendorProductFlat, vendorRepository } from 'api/vendorRepository';
import Card from 'components/card/Card';
import { useEffect, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import ProductTable from './components/ProductTable';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useLocation } from 'react-router-dom';
import { boolean, set } from 'zod';

export default function VendorProduct() {
  const [products, setProducts] = useState<VendorProductFlat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true)
  const [appliedSearch, setAppliedSearch] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [allVendor, setAllVendor] = useState<string[]>([]);
  const [allType, setAllType] = useState<string[]>([]);
  const brandColor = useColorModeValue("brand.500", "brand.400");
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "white");
  const location = useLocation();
  const initialStatusFilter = location.state?.status;

  const fetchPaginatedProducts = async () => {
    setIsLoading(true);

    try {
      const pageNumber = pageIndex + 1;

      const response =
        await vendorRepository.getVendorProductsPaginated(
          pageNumber,
          pageSize,
          vendorFilter,
          typeFilter,
          appliedSearch
        );
      const data = response?.data || [];
      const alldata: VendorProductFlat[] = response.data
      setAllType([
        ...new Set(alldata.map(a => a.productType).filter(boolean))
      ])
      setAllVendor([
        ...new Set(alldata.map(a => a.vendorName).filter(boolean))
      ])
      setProducts(data);

      setTotalCount(response?.totalCount || data.length);

      setHasMore(
        pageNumber * pageSize < (response?.totalCount || 0)
      );

      setIsError(false);
    } catch (error) {
      console.error(error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchPaginatedProducts();
  }, [
    pageIndex,
    pageSize,
    vendorFilter,
    typeFilter,
    appliedSearch
  ]);

  //const hasMore = pageIndex < totalPages - 1;
  const createPages = () => {
    const pages = [];

    for (let i = 0; i <= pageIndex; i++) {
      pages.push(i + 1);
    }

    if (hasMore) {
      pages.push(pageIndex + 2);
    }

    return pages;
  };

  return (
    <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
      <Card px="0px">
        {isLoading ? (
          <Center h="60vh">
            <Spinner size="xl" />
          </Center>
        ) : isError ? (
          <Center py={10}>
            <Text color="red.500">Failed to load products.</Text>
          </Center>
        ) : (
          <ProductTable
            tableData={products}
            onDelete={(deletedId) =>
              setProducts((prev) =>
                prev.filter((p) => p.productId !== deletedId))}
            onProductUpdated={fetchPaginatedProducts}
            initialStatusFilter={initialStatusFilter}

            vendorFilter={vendorFilter}
            setVendorFilter={setVendorFilter}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            searchFilter={searchFilter}
            setSearchFilter={setSearchFilter}
            setAppliedSearch={setAppliedSearch}
            setPageIndex={setPageIndex}
            allVendor={allVendor}
            allType={allType}
          />
        )}
      </Card>

      {/* PAGINATION UI */}
      <Flex
        w="100%"
        justify="space-between"
        px="20px"
        pt="20px"
        pb="20px"
      >
        {/* Left text */}
        <Menu>
          <Text m={2}>showing row per page</Text>
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            colorScheme="gray"
            borderRadius="16px"
            px={5}
            h="44px"
          >
            {pageSize}
          </MenuButton>

          <MenuList zIndex={1000} boxShadow="lg">
            {[10, 20].map((size) => (
              <MenuItem
                key={size}
                onClick={() => {
                  setPageSize(size);
                  setPageIndex(0);
                }}
              >
                {size}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        {/* Pagination buttons */}
        <Stack direction="row" alignSelf="flex-end" spacing="4px" ms="auto">
          <Button
            variant="no-effects"
            onClick={() => setPageIndex(prev => Math.max(0, prev - 1))}
            disabled={pageIndex === 0}
            w="40px"
            h="40px"
            borderRadius="50%"
            bg="transparent"
            border="1px solid"
            borderColor={borderColor}
          >
            <Icon as={MdChevronLeft} w="16px" h="16px" color={textColor} />
          </Button>

          {/* Page numbers */}
          {createPages().map((pageNumber, index) => (
            <Button
              key={index}
              onClick={() => setPageIndex(pageNumber - 1)}
              w="40px"
              h="40px"
              borderRadius="50%"
              bg={pageIndex + 1 === pageNumber ? brandColor : "transparent"}
              border={
                pageIndex + 1 === pageNumber ? "none" : "1px solid lightgray"
              }
            >
              <Text color={pageIndex + 1 === pageNumber ? "#fff" : textColor}>
                {pageNumber}
              </Text>
            </Button>
          ))}

          {/* Next */}
          <Button
            variant="no-effects"
            onClick={() => setPageIndex(prev => prev + 1)}
            disabled={!hasMore}
            w="40px"
            h="40px"
            borderRadius="50%"
            bg="transparent"
            border="1px solid"
            borderColor={borderColor}
          >
            <Icon as={MdChevronRight} w="16px" h="16px" color={textColor} />
          </Button>
        </Stack>
      </Flex>
    </Flex>
  );
}
